import React from 'react';
import { useUser } from '../../../../../redux/user/useUser';

import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import media from 'styled-media-query';

import { TabNavigationLayout } from '../../../../templates/TabNavigationLayout';
import { Fab } from '../../../../atoms/Fab';
import { PageWrapper } from '../../../../atoms/PageWrapper';
import { Breadcrumb } from '../../../../organisms/Breadcrumb';

import { ProgramCompleteModal } from '../ProgramCompleteModal';
import { ProgramElementsList } from '../ProgramElementsList';

import { useProgramSatisfactionModal } from '../ProgramSatisfactionModal';
import { QuestionCreateModal } from '../QuestionCreateModal';

import { FIRST_PAGE, FETCH_LIMIT_MIDDLE } from '../../../../../const/Page';
import {
  useGetQuestionsQuery,
  QuestionSearchType,
  QuestionSortKey,
  QuestionSearchInput,
  useGetProgramSatisfactionLazyQuery,
  ProgramCategory,
} from '../../../../../gen/graphql';
import { QuestionTabContent } from '../../../../organisms/QuestionTabContent';
import { FunctionType, PermissionType } from '../../../../../const/UserPermission';
import { NotFoundPage } from '../../../public/NotFound';
import { getChaptersAndPractices } from '../programUtil';
import { Loader } from '../../../../molecules/Loader';
import ForbiddenPage from '../../../public/Forbidden';
import { useCommonModal } from '../../../../../redux/common_modal/useCommonModal';
import { isCompletedProgram } from '../programUtil';
import { ChapterDetailContent } from './ChapterDetailContent';
import { useChapterDetailContent } from './useChapterDetailContent';

/** タブ */
type TabType = 'content' | 'list' | 'questions';

export function ChapterDetail(props: { tab: TabType }): React.ReactElement {
  const params = useParams<{ program_id: string; chapter_id: string }>();
  const pathProgramID = Number(params.program_id);
  const pathChapterID = Number(params.chapter_id);
  if (Number.isNaN(pathProgramID) || Number.isNaN(pathChapterID)) return NotFoundPage;

  return <ChapterDetailInternal programID={pathProgramID} chapterID={pathChapterID} tab={props.tab} />;
}

interface Props {
  programID: number;
  chapterID: number;
  tab: TabType;
}

function ChapterDetailInternal(props: Props): React.ReactElement {
  const { user, permissionCheck } = useUser();

  // 理解度の取得をスキップするかどうか。
  const skipStudyLog = !permissionCheck(FunctionType.StudyLog, PermissionType.Read);

  // 確認問題を含めるかどうか。
  const chapterIncludedExam =
    permissionCheck(FunctionType.Exam, PermissionType.Read) ||
    permissionCheck(FunctionType.ExamForInstructorAndCoach, PermissionType.Read);

  // 教材と章の内容を取得する。
  const chapterDetailContent = useChapterDetailContent(
    props.programID,
    props.chapterID,
    user.lmsUser?.id,
    skipStudyLog,
    chapterIncludedExam,
  );

  // 目次を抽出する。
  const programElements = getChaptersAndPractices(chapterDetailContent.programData.program);

  // 質問を取得する。
  const { openModal } = useCommonModal();
  const questions = useQuestions(
    props.programID,
    props.chapterID,
    chapterDetailContent.programData.program?.category,
    () => openModal(FunctionType.Question, PermissionType.Create),
  );

  // パンくずリスト情報
  const breadcrumbs: Breadcrumb.Items = [
    {
      label: 'ホーム',
      to: '/home',
    },
    {
      label: '教材',
      to: permissionCheck(FunctionType.ProgramForInstructorAndCoach, PermissionType.Read)
        ? '/programs?type=1'
        : '/programs',
    },
    {
      label: chapterDetailContent.chapterData.chapter?.programElement?.program.title
        ? chapterDetailContent.chapterData.chapter.programElement.program.title
        : '章一覧',
      to: `/programs/${props.programID}/chapters`,
    },
    {
      label: chapterDetailContent.chapterData.chapter?.title
        ? chapterDetailContent.chapterData.chapter.title
        : '現在のページ',
    },
  ];

  // ローダーオーバーレイ表示フラグ
  const showLoader =
    chapterDetailContent.studyLogData.loading ||
    chapterDetailContent.chapterData.loading ||
    questions.data.loading ||
    chapterDetailContent.programData.loading;

  // ダイアログ制御
  const dialogs = useDialogs(props.programID);

  // 何らかの理由(権限不足含む)により章データまたは教材データを取得できない場合は 403 を表示する。
  if (
    (!chapterDetailContent.chapterData.chapter && !chapterDetailContent.chapterData.loading) ||
    (!chapterDetailContent.programData.program && !chapterDetailContent.programData.loading)
  ) {
    return ForbiddenPage;
  }

  return (
    <>
      <Loader display={showLoader} />

      <QuestionCreateModal
        isOpen={questions.isCreateModalOpen}
        onClose={() => questions.setIsCreateModalOpen(false)}
        handleLink={questions.onOpenQuestionFormClick}
      />

      <ProgramCompleteModal
        isOpen={dialogs.isProgramCompleteModalOpen}
        onClose={() => dialogs.setIsProgramCompleteModalOpen(false)}
        programID={props.programID}
        programName={chapterDetailContent.chapterData.chapter?.programElement.program.title}
      />

      <dialogs.ProgramSatisfactionModal />

      <TabNavigationLayout
        pageTitle="教材"
        tabs={[
          {
            label: '本文',
            to: `/programs/${props.programID}/chapters/${props.chapterID}`,
            active: props.tab === 'content',
          },
          {
            label: '目次',
            to: `/programs/${props.programID}/chapters/${props.chapterID}/list`,
            active: props.tab === 'list',
          },
          {
            label: `質問一覧 ${questions.total}件`,
            to: `/programs/${props.programID}/chapters/${props.chapterID}/questions`,
            active: props.tab === 'questions',
          },
        ]}
        unfixed
        metaTitle={chapterDetailContent.metaTitle}
        metaDescription={chapterDetailContent.metaDescription}
      >
        <PageWrapper>
          <Main>
            <StyledBreadcrumb links={breadcrumbs} />
            {props.tab === 'content' ? (
              <ChapterDetailContent
                programID={props.programID}
                chapterID={props.chapterID}
                program={chapterDetailContent.programData.program}
                chapter={chapterDetailContent.chapterData.chapter}
                programExam={chapterDetailContent.chapterData.programExam}
                studyLog={chapterDetailContent.studyLogData.getStudyLog}
                refetchStudyLog={chapterDetailContent.studyLogData.refetch}
                openCompleteModal={dialogs.openCompleteModal}
                hasPermissionToReadStudyLog={permissionCheck(FunctionType.StudyLog, PermissionType.Read)}
                hasPermissionToReadExam={permissionCheck(FunctionType.Exam, PermissionType.Read)}
                hasPermissionToReadExamForInstructorAndCoach={permissionCheck(
                  FunctionType.ExamForInstructorAndCoach,
                  PermissionType.Read,
                )}
                isLoggedIn={user.isLogged}
                personalInfoVisible={true}
                programElementsVisible={true}
              />
            ) : props.tab === 'list' ? (
              <ProgramElementsList
                programElements={programElements}
                currentID={props.chapterID}
                programID={props.programID}
              />
            ) : props.tab === 'questions' ? (
              <>
                {chapterDetailContent.programData.program && (
                  <QuestionTabContent
                    permissionCheck={permissionCheck}
                    questionsTotal={questions.total}
                    questionsItems={questions.items}
                    fetchQuestions={questions.fetch}
                    program={chapterDetailContent.programData.program}
                    chapterID={props.chapterID}
                  />
                )}
              </>
            ) : (
              // ここに来ることはない。
              <></>
            )}
          </Main>
          {(permissionCheck(FunctionType.Question, PermissionType.Create) ||
            permissionCheck(FunctionType.RecommendLight, PermissionType.Read) ||
            permissionCheck(FunctionType.RecommendTeamSubscription, PermissionType.Read) ||
            permissionCheck(FunctionType.RecommendTeamSubscriptionToAdmin, PermissionType.Read)) && (
            <Fab onClick={questions.handleNewQuestionClick}>+ 質問する</Fab>
          )}
        </PageWrapper>
      </TabNavigationLayout>
    </>
  );
}

/** 質問 Question */
function useQuestions(
  programID: number,
  chapterID: number,
  programCategory: ProgramCategory | undefined,
  openUpsellModal: () => boolean,
): {
  data: ReturnType<typeof useGetQuestionsQuery>;
  total: number;
  items: NonNullable<ReturnType<typeof useGetQuestionsQuery>['data']>['getQuestions']['items'];
  fetch: (params: QuestionSearchInput) => void;
  isCreateModalOpen: boolean;
  setIsCreateModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onOpenQuestionFormClick: () => void;
  handleNewQuestionClick: () => void;
} {
  const [isCreateModalOpen, setIsCreateModalOpen] = React.useState(false);

  const questionsData = useGetQuestionsQuery({
    variables: {
      input: {
        type: QuestionSearchType.All,
        page: FIRST_PAGE,
        limit: FETCH_LIMIT_MIDDLE,
        chapterID: chapterID,
        sortKey: QuestionSortKey.CreatedAt,
      },
    },
  });
  const questionsTotal = questionsData.data?.getQuestions.total ?? 0;
  const questionsItems = questionsData.data?.getQuestions.items ?? [];
  const fetchQuestions = (params: QuestionSearchInput) => {
    questionsData.refetch({ input: params });
  };

  const onOpenQuestionFormClick = () => {
    window.open(`/question/new?category_id=${programCategory}&program_id=${programID}&chapter_id=${chapterID}`);
    setIsCreateModalOpen(false);
  };

  const handleNewQuestionClick = () => {
    if (openUpsellModal()) return;
    setIsCreateModalOpen(true);
  };

  return {
    data: questionsData,
    total: questionsTotal,
    items: questionsItems,
    fetch: fetchQuestions,
    isCreateModalOpen: isCreateModalOpen,
    setIsCreateModalOpen: setIsCreateModalOpen,
    onOpenQuestionFormClick: onOpenQuestionFormClick,
    handleNewQuestionClick: handleNewQuestionClick,
  };
}

/** ダイアログの制御 */
function useDialogs(programID: number): {
  openCompleteModal: () => void;
  ProgramSatisfactionModal: React.FC;
  isProgramCompleteModalOpen: boolean;
  setIsProgramCompleteModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isProgramSatisfactionModalOpen: boolean;
  setIsProgramSatisfactionModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
} {
  const [getProgramSatisfaction, { data: programSatisfactionData }] = useGetProgramSatisfactionLazyQuery({
    fetchPolicy: 'network-only',
  });

  const [isProgramCompleteModalOpen, setIsProgramCompleteModalOpen] = React.useState(false);
  const [isProgramSatisfactionModalOpen, setIsProgramSatisfactionModalOpen] = React.useState(false);

  const openCompleteModal = () => {
    getProgramSatisfaction({
      variables: {
        programID: programID,
      },
    }).then((result) => {
      if (!isCompletedProgram(result.data?.program?.programElements)) {
        return;
      }

      if (result.data?.programSatisfaction) {
        // 教材アンケートが回答済みの場合は完了モーダル表示
        setIsProgramCompleteModalOpen(true);
        return;
      }

      // 教材アンケートが未回答の場合はアンケートモーダル表示
      setIsProgramSatisfactionModalOpen(true);
    });
  };

  const { Modal: ProgramSatisfactionModal } = useProgramSatisfactionModal(
    programID,
    isProgramSatisfactionModalOpen,
    () => {
      setIsProgramSatisfactionModalOpen(false);
      setIsProgramCompleteModalOpen(true);
    },
    programSatisfactionData?.program?.title ?? '',
  );

  return {
    openCompleteModal: openCompleteModal,
    ProgramSatisfactionModal: ProgramSatisfactionModal,
    isProgramCompleteModalOpen: isProgramCompleteModalOpen,
    setIsProgramCompleteModalOpen: setIsProgramCompleteModalOpen,
    isProgramSatisfactionModalOpen: isProgramSatisfactionModalOpen,
    setIsProgramSatisfactionModalOpen: setIsProgramSatisfactionModalOpen,
  };
}

const Main = styled.div`
  transition: all 0.2s;
`;

const StyledBreadcrumb = styled(Breadcrumb)`
  margin-bottom: 1rem;

  ${media.lessThan('medium')`
    display: none;
  `}
`;
